import type * as models from '../models'

import type { Collection } from './collection'

export class Selectors<TTypeParams extends models.TypeParams.Constraint> {
  /**
   * A selection that represents the current context of the entity collection.
   *
   * In most cases, the context will be a selection of the selected `tenantId`,
   * selected `zoneId`, selected `appId`, selected `scenarioId`, or all 4.
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public context

  /**
   * Indicates if the entity collection is ready, including all of its plugins
   *
   * @see {@link isReadyIgnoringPlugins}
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly isReady

  /**
   * Indicates the entity collection itself is fully ready, ignoring the current
   * status of the plugins
   *
   * @see {@link isReady}
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly isReadyIgnoringPlugins

  constructor(collection: Collection<TTypeParams>) {
    this.context = collection.feature.state.project(state => state.context)

    /**
     * Determines if the entity collection is awaiting context. This occurs when
     * {@link usingContext} is `true` and {@link context} remains `undefined`.
     */
    const waitingOnContext = this.context.project(
      context => collection.usingContext && context === undefined,
    )

    this.isReadyIgnoringPlugins = waitingOnContext.project(
      _waitingOnContext => !_waitingOnContext,
    )
    this.isReady = this.isReadyIgnoringPlugins.project(
      collection.plugins.feature.selectors.allPluginsAreReady.asSelector,
      (isReadyIgnoringPlugins, allPluginsAreReady) =>
        isReadyIgnoringPlugins && allPluginsAreReady,
    )
  }
}
